import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Clock, Zap, Mail, Monitor, BarChart3, ExternalLink, Archive, ArrowLeft, Filter, ChevronDown, ChevronUp, Loader2, Trash2, LogOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  Intelligence: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: BarChart3 },
  Outreach: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Mail },
  CRM: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: ExternalLink },
  System: { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: Monitor },
};

const PRIORITY_STYLES = {
  high: { dot: 'bg-red-500', label: 'High', badge: 'bg-red-50 text-red-700 border-red-200' },
  medium: { dot: 'bg-amber-500', label: 'Medium', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  low: { dot: 'bg-gray-400', label: 'Low', badge: 'bg-gray-50 text-gray-500 border-gray-200' },
};

const fetchAuth = async (url, options = {}) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, {
    ...options,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const formatDate = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  if (hrs < 48) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/notifications/?limit=100&sort=${sortOrder}`;
      if (categoryFilter !== 'all') url += `&category=${categoryFilter}`;
      const d = await fetchAuth(url);
      setNotifications(d.notifications);
      setTotal(d.total);
    } catch {}
    setLoading(false);
  }, [categoryFilter, sortOrder]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markRead = async (id) => {
    await fetchAuth(`${API_URL}/api/notifications/${id}/read`, { method: 'PATCH' });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  const markUnread = async (id) => {
    await fetchAuth(`${API_URL}/api/notifications/${id}/unread`, { method: 'PATCH' });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: false } : n));
  };

  const archiveNotif = async (id) => {
    await fetchAuth(`${API_URL}/api/notifications/${id}/archive`, { method: 'PATCH' });
    setNotifications(prev => prev.filter(n => n.id !== id));
    setTotal(prev => prev - 1);
  };

  const markAllRead = async () => {
    await fetchAuth(`${API_URL}/api/notifications/mark-all-read`, { method: 'POST' });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const bulkMarkRead = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    await fetchAuth(`${API_URL}/api/notifications/bulk-read`, { method: 'POST', body: JSON.stringify({ notification_ids: ids }) });
    setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: true } : n));
    setSelectedIds(new Set());
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === notifications.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(notifications.map(n => n.id)));
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const categories = ['all', 'Intelligence', 'Outreach', 'CRM', 'System'];

  const handleLogout = () => {
    localStorage.removeItem('backoffice_token');
    localStorage.removeItem('backoffice_user');
    navigate('/backoffice/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0A1F44] text-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <button onClick={() => navigate('/backoffice')} className="hover:text-[#D4AF37] transition-colors" data-testid="back-to-dashboard">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <Bell className="h-5 w-5 text-[#D4AF37]" />
              <h1 className="text-xl font-bold">Notifications</h1>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount} unread</span>
              )}
            </div>
            <p className="text-white/60 text-sm ml-8">Intelligence alerts, outreach reminders, and system updates</p>
          </div>
          <button onClick={handleLogout} className="p-2 hover:bg-white/10 rounded-lg" data-testid="notifications-logout-button" title="Sign Out">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          {/* Category Filters */}
          <div className="flex items-center gap-2" data-testid="category-filters">
            <Filter className="h-4 w-4 text-gray-400" />
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                  categoryFilter === cat
                    ? 'bg-[#0A1F44] text-white border-[#0A1F44]'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`filter-${cat.toLowerCase()}`}
              >
                {cat === 'all' ? `All (${total})` : cat}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSortOrder(s => s === 'newest' ? 'oldest' : 'newest')}
              className="px-3 py-1 text-xs font-medium rounded-full border border-gray-200 text-gray-600 hover:border-gray-400 flex items-center gap-1"
              data-testid="sort-toggle"
            >
              {sortOrder === 'newest' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
              {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
            </button>
            {selectedIds.size > 0 && (
              <button
                onClick={bulkMarkRead}
                className="px-3 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 flex items-center gap-1"
                data-testid="bulk-mark-read"
              >
                <CheckCheck className="h-3 w-3" /> Mark Selected Read ({selectedIds.size})
              </button>
            )}
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="px-3 py-1 text-xs font-medium rounded-full bg-[#D4AF37]/10 text-[#0A1F44] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/20 flex items-center gap-1"
                data-testid="mark-all-read-page"
              >
                <CheckCheck className="h-3 w-3" /> Mark All Read
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-20">
            <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No notifications{categoryFilter !== 'all' ? ` in ${categoryFilter}` : ''}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {/* Select All */}
            <div className="flex items-center gap-2 px-4 py-2 text-xs text-gray-400">
              <input
                type="checkbox"
                checked={selectedIds.size === notifications.length && notifications.length > 0}
                onChange={selectAll}
                className="rounded border-gray-300"
              />
              <span>Select all</span>
            </div>

            {notifications.map((n) => {
              const cat = CATEGORY_CONFIG[n.category] || CATEGORY_CONFIG.System;
              const CatIcon = cat.icon;
              const pri = PRIORITY_STYLES[n.priority] || PRIORITY_STYLES.medium;

              return (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border transition-all cursor-pointer hover:shadow-sm ${
                    !n.is_read
                      ? 'bg-white border-blue-100 shadow-sm'
                      : 'bg-white/60 border-gray-100'
                  }`}
                  onClick={() => { if (n.link) navigate(n.link); if (!n.is_read) markRead(n.id); }}
                  data-testid={`notif-row-${n.id}`}
                >
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedIds.has(n.id)}
                    onChange={(e) => { e.stopPropagation(); toggleSelect(n.id); }}
                    className="rounded border-gray-300 mt-1"
                    onClick={(e) => e.stopPropagation()}
                  />

                  {/* Priority + Icon */}
                  <div className="flex flex-col items-center gap-1.5 pt-0.5">
                    <div className={`w-2.5 h-2.5 rounded-full ${!n.is_read ? pri.dot : 'bg-gray-200'}`} />
                    <CatIcon className="h-4 w-4 text-gray-400" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] px-1.5 py-0 rounded-full border font-medium ${cat.color}`}>{n.category}</span>
                      <span className={`text-[10px] px-1.5 py-0 rounded-full border font-medium ${pri.badge}`}>{pri.label}</span>
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5 ml-auto">
                        <Clock className="h-2.5 w-2.5" /> {formatDate(n.created_at)}
                      </span>
                    </div>
                    <p className={`text-sm leading-snug ${!n.is_read ? 'font-semibold text-[#0A1F44]' : 'font-medium text-gray-600'}`}>
                      {n.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{n.description}</p>
                    {n.link && (
                      <span className="text-[10px] text-blue-500 mt-1 inline-block hover:underline">Open related page</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-1">
                    {!n.is_read ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                        className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-500 transition-colors"
                        title="Mark as read"
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); markUnread(n.id); }}
                        className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors"
                        title="Mark as unread"
                      >
                        <Bell className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); archiveNotif(n.id); }}
                      className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                      title="Archive"
                    >
                      <Archive className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
